import { datadogRum } from '@datadog/browser-rum';
import { Routes } from '@datadog/browser-rum-react/react-router-v6';
import { useFlag } from '@lemonade-hq/flags';
import { Env, useComplianceProvider, useInfraContext } from '@lemonade-hq/nuiza';
import { Flag, initSessionReplay, trackAppCrash, UniclientApp, useNavContext } from '@lemonade-hq/uniclient-app';
import { parseUniclientPresetUrl } from '@lemonade-hq/uniclient-chat';
import { UniclientWizard, useGetUrlParams } from '@lemonade-hq/uniclient-chat-client';
import { isNativeApp } from '@lemonade-hq/uniclient-mobile-bridge';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Route, BrowserRouter as Router } from 'react-router-dom';
import {
  analyticsConfig,
  authConfig,
  flagsConfig,
  googleMapConfig,
  infraConfig,
  localeConfig,
  navConfig,
  realtimeConfig,
  securityConfig,
} from './config';
import { useUniclientPersistence } from './hooks/useUniclientPersistence';
import { NotFoundPage } from './NotFoundPage/NotFoundPage';

const LazyCookieConsent = React.lazy(
  async () => await import('@lemonade-hq/uniclient-app').then(module => ({ default: module.CookieConsent })),
);

const DEV_ENVIRONMENTS = [Env.Development, 'local'];

const WizardWrapper: React.FC = () => {
  const infra = useInfraContext();
  const { params, error } = useUniclientPersistence();
  const getUrlParams = useGetUrlParams();
  const clientParams = useMemo(() => getUrlParams(window.location), [getUrlParams]);
  const isSessionReplayEnabled = useFlag(Flag.SessionReplayEnabled);

  const { nav } = useNavContext();

  const { isConsentBannerShown, cookieTypesEnabledList } = useComplianceProvider();

  const isDev = useMemo(() => DEV_ENVIRONMENTS.includes(infra.env), [infra.env]);

  useEffect(() => {
    if (isSessionReplayEnabled) {
      initSessionReplay();
    }
  }, [isSessionReplayEnabled]);

  useEffect(() => {
    if (cookieTypesEnabledList.includes('performance')) {
      datadogRum.setTrackingConsent('granted');
    }
  }, [cookieTypesEnabledList]);

  const onAccept = useCallback(() => {
    datadogRum.setTrackingConsent('granted');
  }, []);

  const onDecline = useCallback(() => {
    datadogRum.setTrackingConsent('not-granted');
  }, []);

  useEffect(() => {
    if (error == null) return;

    trackAppCrash(error.message);
  }, [error]);

  if (error != null) {
    return <NotFoundPage />;
  }

  if (params != null)
    return (
      <>
        <UniclientWizard chatEnginePersistence={params} clientParams={clientParams} />
        {isConsentBannerShown && !isDev && !isNativeApp() && (
          <React.Suspense fallback={null}>
            <LazyCookieConsent baseUrl={nav.monolithUrl} onAccept={onAccept} onDecline={onDecline} />
          </React.Suspense>
        )}
      </>
    );

  return null;
};

export const App: React.FC = () => {
  const infra = infraConfig();

  const { serverBaseUrl } = useMemo(() => {
    try {
      return parseUniclientPresetUrl(window.location, infra.env, infra.envId);
    } catch (e: unknown) {
      return { serverBaseUrl: '' };
    }
  }, [infra]);

  return (
    <Router>
      <Routes>
        <Route
          element={
            <UniclientApp
              analytics={analyticsConfig()}
              auth={authConfig(serverBaseUrl)}
              fallback={<NotFoundPage />}
              flags={flagsConfig()}
              googleMaps={googleMapConfig()}
              infra={infra}
              locale={localeConfig()}
              nav={navConfig(infra)}
              realtime={realtimeConfig()}
              security={securityConfig()}
            >
              <WizardWrapper />
            </UniclientApp>
          }
          path="/*"
        />
      </Routes>
    </Router>
  );
};
